/**
 * Utility functions for business profile form handling
 * Pure functions with no React dependencies
 */

export interface ParsedAddress {
  address_line_1: string;
  address_line_2: string;
  city: string;
  state_province: string;
  postal_code: string;
  country: string;
}

/**
 * Parse Google Places API address components into structured address fields
 * Supports both new API format (componentType, componentName) and old format (types, long_name)
 */
export function parseAddressComponents(place: any): ParsedAddress {
  // console.log('🔍 parseAddressComponents - place:', place);
  // console.log('🔍 parseAddressComponents - address_components:', place?.address_components);
  
  if (!place || !place.address_components || !Array.isArray(place.address_components)) {
    // console.warn('⚠️ No address_components found in place object');
    return {
      address_line_1: '',
      address_line_2: '',
      city: '',
      state_province: '',
      postal_code: '',
      country: '',
    };
  }

  const components = place.address_components;
  // console.log('🔍 Components array:', components);
  // console.log('🔍 First component:', components[0]);
  
  let addressLine1 = '';
  let addressLine2 = '';
  let city = '';
  let stateProvince = '';
  let postalCode = '';
  let country = '';

  // Helper to get component by type
  // Google Places API (New) uses 'componentType' (string) instead of 'types' (array)
  const getComponent = (types: string[]) => {
    const found = components.find((comp: any) => {
      // New API uses 'componentType' as a string, not 'types' as an array
      const compType = comp.componentType || comp.types;
      
      if (!compType) {
        // console.log('⚠️ Component missing componentType:', comp);
        return false;
      }
      
      // Check if componentType matches any of our target types
      const hasType = types.some(type => {
        if (typeof compType === 'string') {
          return compType === type;
        } else if (Array.isArray(compType)) {
          return compType.includes(type);
        }
        return false;
      });
      
      if (hasType) {
        // console.log(`✅ Found component for types ${types.join(', ')}:`, comp);
      }
      return hasType;
    });
    return found;
  };

  // Helper to extract text from component
  // Google Places API (New) uses componentName.text, old API uses long_name
  const getComponentText = (comp: any, preferShort = false) => {
    if (!comp) return '';
    // New API format: componentName.text or componentName.shortText
    if (comp.componentName) {
      return preferShort 
        ? (comp.componentName.shortText || comp.componentName.text || '')
        : (comp.componentName.text || comp.componentName.shortText || '');
    }
    // Old API format or fallback
    return comp.shortText || comp.longText || comp.long_name || comp.short_name || '';
  };

  // Extract street number and route for address_line_1
  const streetNumber = getComponent(['street_number']);
  const route = getComponent(['route']);
  if (streetNumber || route) {
    const streetNum = getComponentText(streetNumber);
    const routeText = getComponentText(route);
    addressLine1 = [streetNum, routeText].filter(Boolean).join(' ').trim();
    // console.log('📍 Address Line 1:', addressLine1, 'from:', { streetNumber, route });
  }

  // Extract subpremise for address_line_2
  const subpremise = getComponent(['subpremise']);
  if (subpremise) {
    addressLine2 = getComponentText(subpremise);
    // console.log('📍 Address Line 2:', addressLine2);
  }

  // Extract city (locality or administrative_area_level_3)
  const locality = getComponent(['locality']);
  const adminArea3 = getComponent(['administrative_area_level_3']);
  city = getComponentText(locality) || getComponentText(adminArea3);
  // console.log('📍 City:', city, 'from:', { locality, adminArea3 });

  // Extract state/province (administrative_area_level_1)
  const adminArea1 = getComponent(['administrative_area_level_1']);
  stateProvince = getComponentText(adminArea1, true) || getComponentText(adminArea1); // Prefer short for state codes
  // console.log('📍 State/Province:', stateProvince, 'from:', adminArea1);

  // Extract postal code
  const postalCodeComp = getComponent(['postal_code']);
  postalCode = getComponentText(postalCodeComp);
  // console.log('📍 Postal Code:', postalCode, 'from:', postalCodeComp);

  // Extract country
  const countryComp = getComponent(['country']);
  country = getComponentText(countryComp);
  // console.log('📍 Country:', country, 'from:', countryComp);

  const result: ParsedAddress = {
    address_line_1: addressLine1,
    address_line_2: addressLine2,
    city,
    state_province: stateProvince,
    postal_code: postalCode,
    country,
  };
  
  // console.log('📍 Final parsed result:', result);
  return result;
}

/**
 * Normalize brand_voice value to array format
 * Handles string (comma-separated or single), array, or null/undefined
 */
export function normalizeBrandVoice(value: any): string[] {
  if (Array.isArray(value)) {
    return value.filter(v => v && v.trim()); // Filter out empty strings
  }
  if (typeof value === 'string' && value.trim()) {
    // If it's a comma-separated string, split it; otherwise treat as single value
    return value.includes(',') ? value.split(',').map(v => v.trim()).filter(v => v) : [value.trim()];
  }
  return [];
}

/**
 * Normalize products_services value to array format
 * Handles string (comma-separated), array, or null/undefined
 */
export function normalizeProductsServices(value: any): string[] {
  if (Array.isArray(value)) {
    return value.filter(v => v && v.trim());
  }
  if (typeof value === 'string' && value.trim()) {
    return value.split(',').map(v => v.trim()).filter(v => v);
  }
  return [];
}

/**
 * Get a user-friendly error message for invalid URLs
 */
export function getUrlValidationError(urlString: string): string | null {
  if (!urlString || typeof urlString !== 'string') {
    return 'Please enter a valid website URL';
  }

  const trimmed = urlString.trim();
  if (!trimmed) {
    return 'Please enter a website URL';
  }

  // Check for common incomplete patterns and provide specific messages
  if (trimmed === 'https://' || trimmed === 'http://') {
    return 'Please enter a complete URL with a domain (e.g., https://bizcontent.ai)';
  }

  if (trimmed === 'https://www' || trimmed === 'http://www' || trimmed === 'https://www.' || trimmed === 'http://www.') {
    return 'Please enter a complete URL with a domain after www (e.g., https://www.bizcontent.ai)';
  }

  if (trimmed === 'www' || trimmed === 'www.') {
    return 'Please enter a complete URL with a domain (e.g., www.bizcontent.ai or https://www.bizcontent.ai)';
  }

  if (trimmed.startsWith('localhost') || trimmed.includes('localhost:')) {
    return 'Localhost URLs are not allowed. Please enter a public website URL';
  }

  // URLs without scheme are allowed - backend will normalize them
  // Just check if it looks like a valid domain
  if (!trimmed.startsWith('http://') && !trimmed.startsWith('https://')) {
    // If it contains a dot and doesn't start with localhost, it might be a valid domain
    if (trimmed.includes('.') && !trimmed.startsWith('localhost')) {
      return null; // Allow it - backend will normalize
    }
    // Otherwise it's likely invalid
    return 'Please enter a valid website URL (e.g., bizcontent.ai or https://bizcontent.ai)';
  }

  // If we get here, it's likely a malformed URL
  return 'Please enter a valid, complete website URL (e.g., https://www.bizcontent.ai)';
}

/**
 * Validate URL format
 * Returns true if URL is valid, uses http:// or https:// protocol, and has a complete hostname
 * Matches backend validation logic from validate_and_normalize_url
 */
export function isValidUrl(urlString: string): boolean {
  if (!urlString || typeof urlString !== 'string') {
    return false;
  }

  const trimmed = urlString.trim();
  if (!trimmed) {
    return false;
  }

  // Reject empty URL
  if (trimmed === '') {
    return false;
  }

  // Reject URLs without domain (just scheme)
  if (trimmed === 'https://' || trimmed === 'http://') {
    return false;
  }

  // Reject incomplete domain patterns
  // "www" alone is invalid
  if (trimmed === 'www' || trimmed === 'www.') {
    return false;
  }

  // Reject "https://www" (incomplete - no domain after www)
  if (trimmed === 'https://www' || trimmed === 'http://www') {
    return false;
  }

  try {
    // If URL doesn't start with http:// or https://, normalize it for validation
    // Backend accepts URLs without scheme and normalizes them, so frontend should too
    let urlToValidate = trimmed;
    if (!trimmed.startsWith('http://') && !trimmed.startsWith('https://')) {
      // Normalize by adding https:// prefix for validation
      urlToValidate = `https://${trimmed}`;
    }

    const url = new URL(urlToValidate);
    
    // Must use http or https protocol
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      return false;
    }

    // Must have a hostname
    const hostname = url.hostname.trim();
    if (!hostname) {
      return false;
    }

    // Reject localhost (backend doesn't allow business websites on localhost)
    if (hostname === 'localhost' || hostname.startsWith('localhost:')) {
      return false;
    }

    // Hostname should not end with a dot (incomplete domain)
    if (hostname.endsWith('.')) {
      return false;
    }

    // Hostname must have at least one dot (for domain) - no single-word hostnames allowed
    // This prevents "https://www" (parsed as hostname "www") from passing
    if (!hostname.includes('.')) {
      return false;
    }

    // Additional check: if hostname is just "www" (shouldn't happen due to above check, but double-check)
    if (hostname === 'www') {
      return false;
    }

    // URLs without scheme are now allowed - backend will normalize them
    return true;
  } catch {
    return false;
  }
}

